import { useCallback, useEffect, useState } from 'react';
import { isNativeApp } from './native-bridge';

/**
 * Whether the app should refuse to run because location is off.
 *
 * Only inside the Android shell. A web browser cannot report location in the
 * background, so requiring it at a desk would lock out office staff for no
 * gain — `isNativeApp()` keeps the gate to the phones the rule is about.
 *
 * It blocks on one thing only: the geolocation permission being *denied*. Not
 * on `prompt` (undecided — the reporter asks, and a denial then flips this),
 * not on an unsupported Permissions API, not on a transient read error. The
 * cost of a wrong block is a field technician who cannot open the app to
 * clock in or be seen, so the gate fails open on every uncertainty and shuts
 * only on a definite refusal.
 *
 * The device's own location switch, separate from the app's permission, is not
 * read here: the only web signal for it is a position attempt that fails the
 * same way indoors as it does when the switch is off, and blocking on that
 * would strand people inside buildings. The persistent recording notice and
 * the terms carry that part.
 */
export function useLocationGate(): { blocked: boolean; recheck: () => void } {
  const [blocked, setBlocked] = useState(false);

  const query = useCallback((): Promise<PermissionStatus> | null => {
    if (!isNativeApp()) return null;
    const permissions = navigator.permissions;
    if (!permissions?.query) return null;
    try {
      return permissions.query({ name: 'geolocation' as PermissionName });
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;
    let status: PermissionStatus | null = null;

    const onChange = () => {
      if (!cancelled && status) setBlocked(status.state === 'denied');
    };

    const check = () => {
      const pending = query();
      if (!pending) return; // cannot tell → allow
      pending
        .then((next) => {
          if (cancelled) return;
          status?.removeEventListener('change', onChange);
          status = next;
          status.addEventListener('change', onChange);
          setBlocked(next.state === 'denied');
        })
        .catch(() => undefined); // fail open
    };

    check();

    // Returning from the system settings screen is a visibility change, not a
    // navigation — re-check so the block clears the moment they come back with
    // location on.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      status?.removeEventListener('change', onChange);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [query]);

  const recheck = useCallback(() => {
    const pending = query();
    if (!pending) return;
    pending.then((next) => setBlocked(next.state === 'denied')).catch(() => undefined);
  }, [query]);

  return { blocked, recheck };
}
